import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  where,
  getDoc,
  arrayUnion
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { ShieldAlert, CheckCircle, Clock, Search, Filter, MessageSquare, X, Gavel, Scale, AlertTriangle, ArrowRight, User, Home, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

const Disputes = () => {
  const { colors } = useTheme();
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [filter, setFilter] = useState('all'); 
  const [filedByFilter, setFiledByFilter] = useState('all'); 
  const [typeFilter, setTypeFilter] = useState('all'); 
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [hostData, setHostData] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const disputesQuery = filter === 'all'
      ? query(collection(db, 'disputes'), orderBy('createdAt', 'desc'))
      : query(
        collection(db, 'disputes'),
        where('status', '==', filter),
        orderBy('createdAt', 'desc')
      );

    const unsubscribe = onSnapshot(disputesQuery, (snapshot) => {
      const disputesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        resolvedAt: doc.data().resolvedAt?.toDate(),
      }));
      setDisputes(disputesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [filter]);

  useEffect(() => {
    if (!selectedDispute) return;
    const latestDispute = disputes.find((dispute) => dispute.id === selectedDispute.id);
    if (latestDispute) setSelectedDispute(latestDispute);
  }, [disputes, selectedDispute]);

  useEffect(() => {
    if (!selectedDispute) {
      setBookingData(null);
      setHostData(null);
      setGuestData(null);
      setPropertyData(null);
      return;
    }

    const fetchRelatedData = async () => {
      setLoadingBooking(true);
      try {
        let bookingSnap = await getDoc(doc(db, 'bookings', selectedDispute.bookingId));
        if (!bookingSnap.exists()) {
          bookingSnap = await getDoc(doc(db, 'bookings', selectedDispute.bookingId));
        }
        if (bookingSnap.exists()) {
          const bd = { id: bookingSnap.id, ...bookingSnap.data() };
          setBookingData(bd);
          // Fetch property info from the booking's propertyId
          const propId = bd.propertyId || selectedDispute.propertyId;
          if (propId) {
            try {
              const propSnap = await getDoc(doc(db, 'properties', propId));
              if (propSnap.exists()) setPropertyData({ id: propSnap.id, ...propSnap.data() });
            } catch (_) {}
          }
        }

        if (selectedDispute.hostId) {
          const hostSnap = await getDoc(doc(db, 'users', selectedDispute.hostId));
          if (hostSnap.exists()) setHostData(hostSnap.data());
        }
        if (selectedDispute.guestId) {
          const guestSnap = await getDoc(doc(db, 'users', selectedDispute.guestId));
          if (guestSnap.exists()) setGuestData(guestSnap.data());
        }
      } catch (err) {
        console.error('Error fetching booking data:', err);
      } finally {
        setLoadingBooking(false);
      }
    };

    fetchRelatedData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDispute?.id]);

  const handleResolveDispute = async (disputeId, resolution, refundAmount = null, extraData = {}) => {
    setResolving(true);
    try {
      const shouldCreateRefund = ['partialRefund', 'fullRefund', 'hostDeductionWithRefund'].includes(resolution);
      const finalRefundAmount = resolution === 'fullRefund'
        ? (bookingData?.totalAmount || refundAmount || selectedDispute?.requestedAmount || 0)
        : refundAmount;
      let refundRef = null;

      if (shouldCreateRefund) {
        if (!finalRefundAmount || finalRefundAmount <= 0) {
          throw new Error('A valid refund amount is required.');
        }

        refundRef = await addDoc(collection(db, 'refunds'), {
          bookingId: selectedDispute.bookingId,
          paymentId: bookingData?.paymentId || '',
          requestedBy: selectedDispute.guestId,
          reason: 'dispute',
          amount: finalRefundAmount,
          approvedAmount: finalRefundAmount,
          status: 'approved',
          processedBy: 'admin',
          notes: `Dispute resolution: ${resolution}`,
          metadata: {
            disputeId,
            adminResolution: resolution,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      const updateData = {
        status: 'resolved',
        resolution: resolution,
        resolvedBy: 'admin', 
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...extraData,
      };

      if (finalRefundAmount !== null) {
        updateData.refundAmount = finalRefundAmount;
      }

      if (refundRef) {
        updateData.refundId = refundRef.id;
      }

      await updateDoc(doc(db, 'disputes', disputeId), updateData);

      if (refundRef) {
        try {
          const processApprovedRefund = httpsCallable(functions, 'processApprovedRefund');
          await processApprovedRefund({ refundId: refundRef.id });
          toast.success('Dispute resolved and refund sent through Moyasar.');
        } catch (refundError) {
          console.error('Error processing dispute refund:', refundError);
          toast.error(`Dispute resolved, but refund processing failed: ${refundError.message}`);
        }
      } else {
        toast.success('Dispute resolved successfully!');
      }

      setSelectedDispute(null);
    } catch (error) {
      console.error('Error resolving dispute:', error);
      toast.error('Failed to resolve dispute.');
    } finally {
      setResolving(false);
    }
  };

  const handleSendMessage = async (disputeId) => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const newMessageObj = {
        id: Date.now().toString(),
        senderId: 'admin', 
        senderRole: 'admin',
        message: newMessage.trim(),
        timestamp: new Date(),
      };

      await updateDoc(doc(db, 'disputes', disputeId), {
        messages: arrayUnion(newMessageObj),
        updatedAt: serverTimestamp(),
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const getStatusBadge = (status) => {
    const isResolved = status === 'resolved';
    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
        style={{ 
           backgroundColor: isResolved ? colors.successGhost : colors.warningGhost, 
           color: isResolved ? colors.success : colors.warning, 
           border: `1px solid ${isResolved ? colors.success : colors.warning}30` 
        }}
      >
        {isResolved ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {isResolved ? 'Resolved' : 'Reviewing'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeLabels = {
      propertyCondition: 'Property Condition',
      amenitiesMissing: 'Missing Amenities',
      cleanliness: 'Cleanliness',
      safety: 'Safety',
      hostBehavior: 'Host Behavior',
      overcharge: 'Billing Dispute',
      securityDepositClaim: 'Security Deposit',
      noiseComplaint: 'Noise Complaint',
      propertyDamage: 'Property Damage',
      ruleViolation: 'Rule Violation',
      unauthorizedGuests: 'Unauthorized Guests',
      earlyTermination: 'Early Termination',
      other: 'Other'
    };

    return (
      <span className="px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wider uppercase shadow-sm" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
        {typeLabels[type] || type}
      </span>
    );
  };

  const formatCurrencyLocal = (amount) => {
    return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR' }).format(amount || 0);
  };

  const formatDateTimeLocal = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  const filteredDisputes = disputes.filter(d => {
    if (filedByFilter !== 'all' && (d.filedBy || 'guest') !== filedByFilter) return false;
    if (typeFilter !== 'all' && d.type !== typeFilter) return false;
    if (searchTerm) {
       const lowerTerm = searchTerm.toLowerCase();
       if (!(d.bookingId || '').toLowerCase().includes(lowerTerm) && !(d.id || '').toLowerCase().includes(lowerTerm)) {
          return false;
       }
    }
    return true;
  });

  const kpis = [
    { id: 'all', label: 'Total Disputes', value: disputes.length, icon: Scale, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'open', label: 'Needs Resolution', value: disputes.filter(d => d.status === 'open').length, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'resolved', label: 'Closed & Resolved', value: disputes.filter(d => d.status === 'resolved').length, icon: ShieldCheck, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Dispute Center
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review, investigate, and resolve arbitrations between guests and hosts.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filter === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilter(kpi.id)}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div 
                    className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-[18px] h-[18px] text-white" />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Powerful Filters Grid */}
      <div className="card-premium p-4 md:p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
            
            <div className="relative">
               <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
               <input
                 type="text"
                 placeholder="Search Reference or BKG..."
                 className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                 style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                 onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
               />
            </div>

            <div className="relative">
                <Filter className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
                <select
                  value={filedByFilter}
                  onChange={(e) => setFiledByFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[13px] font-medium appearance-none focus:outline-none transition-all cursor-pointer"
                  style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                  onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                  onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                >
                  <option value="all">Filer: All Originators</option>
                  <option value="guest">Filer: Guest</option>
                  <option value="host">Filer: Host</option>
                </select>
            </div>

            <div className="relative">
                <Gavel className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[13px] font-medium appearance-none focus:outline-none transition-all cursor-pointer"
                  style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                  onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                  onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                >
                    <option value="all">Category: All Topics</option>
                    <optgroup label="Guest Complaints">
                      <option value="cleanliness">Cleanliness</option>
                      <option value="safety">Safety Issue</option>
                      <option value="amenities">Missing Amenities</option>
                      <option value="description">Inaccurate Description</option>
                      <option value="hostBehavior">Host Behavior</option>
                      <option value="noise">Noise Issue</option>
                      <option value="other">Other</option>
                    </optgroup>
                    <optgroup label="Host Complaints">
                      <option value="noiseComplaint">Noise Complaint (Host)</option>
                      <option value="propertyDamage">Property Damage</option>
                      <option value="ruleViolation">Rule Violation</option>
                      <option value="securityDepositClaim">Security Deposit</option>
                      <option value="unauthorizedGuests">Unauthorized Guests</option>
                      <option value="earlyTermination">Early Termination</option>
                    </optgroup>
                </select>
            </div>

            {(filedByFilter !== 'all' || typeFilter !== 'all' || searchTerm) && (
               <button
                  onClick={() => { setFiledByFilter('all'); setTypeFilter('all'); setSearchTerm(''); }}
                  className="w-full md:w-auto px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all ml-auto block"
                  style={{ backgroundColor: colors.errorGhost, color: colors.error }}
               >
                  Clear Filters
               </button>
            )}

        </div>
      </div>

      {/* Disputes Data Table */}
      <div className="card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
         {loading ? (
             <div className="flex flex-col items-center justify-center p-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
                <p className="mt-4 text-[14px] font-medium" style={{ color: colors.textSecondary }}>Syncing disputes...</p>
             </div>
        ) : filteredDisputes.length === 0 ? (
             <div className="flex flex-col items-center justify-center p-16">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
                  <ShieldAlert className="w-8 h-8" style={{ color: colors.textMuted }} />
               </div>
               <h3 className="text-[15px] font-bold" style={{ color: colors.text }}>No resolutions required</h3>
               <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>There are no disputes matching your current filter set.</p>
             </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Brief / Reference</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Dispute Context</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Originator</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDisputes.map((dispute, index) => (
                   <tr 
                     key={dispute.id} 
                     className="group transition-colors cursor-pointer hover:bg-opacity-80"
                     style={{ borderBottom: index < filteredDisputes.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                     onClick={() => setSelectedDispute(dispute)}
                   >
                     <td className="px-6 py-4 whitespace-nowrap">
                         <div className="mb-2">{getTypeBadge(dispute.type)}</div>
                         <div className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>Filed: {formatDateTimeLocal(dispute.createdAt)}</div>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-[12px] font-bold font-mono tracking-wide" style={{ color: colors.text }}>BKG: {dispute.bookingId?.slice(0,12) || 'N/A'}</div>
                        {dispute.propertyTitle && (
                          <div className="text-[12px] font-semibold truncate max-w-[160px] mt-1" style={{ color: colors.textSecondary }}>{dispute.propertyTitle}</div>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                           {dispute.refundRequested && <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-green-500/10 text-green-500 border border-green-500/20">Refund Req</span>}
                           {dispute.blocksEarnings && <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-red-500/10 text-red-500 border border-red-500/20">Funds Blocked</span>}
                           {dispute.affectsSecurityDeposit && <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-purple-500/10 text-purple-500 border border-purple-500/20">Deposit Claim</span>}
                        </div>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                         <div className={`inline-flex flex-col items-center justify-center w-10 h-10 rounded-xl shadow-sm text-white ${
                            (dispute.filedBy || 'guest') === 'host' ? 'bg-gradient-to-br from-orange-400 to-orange-600' : 'bg-gradient-to-br from-blue-400 to-blue-600'
                         }`}>
                             {(dispute.filedBy || 'guest') === 'host' ? <Home className="w-5 h-5"/> : <User className="w-5 h-5"/>}
                         </div>
                         <div className="text-[11px] font-bold tracking-wider uppercase mt-1 text-center w-10" style={{ color: colors.textSecondary }}>{(dispute.filedBy || 'guest')}</div>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(dispute.status)}
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          className="px-3.5 py-2 rounded-xl text-[12px] font-bold transition-all shadow-sm group-hover:scale-105 inline-flex items-center gap-1.5"
                          style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}
                        >
                           {dispute.status === 'open' ? 'Arbitrate' : 'Archive'} <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                     </td>
                   </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Mega Resolution Modal */}
      {selectedDispute && (
         <div className="admin-modal-overlay fixed inset-0 z-[70] p-4 sm:p-6 lg:p-8 overflow-y-auto animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(12px)' }}>
            <div className="min-h-full flex items-center justify-center">
              <div className="admin-modal-card rounded-3xl max-w-5xl w-full flex flex-col shadow-2xl relative overflow-hidden transform transition-all animate-fadeInScale" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
               
               {/* Modal Header */}
               <div className="flex-shrink-0 px-5 sm:px-6 py-5 border-b flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center" style={{ borderColor: colors.border, backgroundColor: ['resolved'].includes(selectedDispute.status) ? colors.successGhost : colors.backgroundSecondary }}>
                   <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md text-white ${['resolved'].includes(selectedDispute.status) ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-indigo-500 to-blue-600'}`}>
                         {['resolved'].includes(selectedDispute.status) ? <ShieldCheck className="w-6 h-6" /> : <Gavel className="w-6 h-6" />}
                      </div>
                      <div>
                         <h2 className="text-xl font-black tracking-tight" style={{ color: colors.text }}>Arbitration Dossier</h2>
                         <div className="flex gap-2 items-center mt-0.5">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-opacity-70" style={{ color: colors.textSecondary }}>REF: {selectedDispute.id}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                            {getTypeBadge(selectedDispute.type)}
                         </div>
                      </div>
                   </div>
                   <button onClick={() => setSelectedDispute(null)} className="p-2.5 rounded-full hover:rotate-90 transition-all duration-300" style={{ backgroundColor: colors.background }}>
                       <X className="w-5 h-5" style={{ color: colors.text }} />
                   </button>
               </div>

               {/* Modal Body */}
               <div className="admin-modal-scroll flex-1 flex flex-col md:flex-row">
                   
                   {/* Left Panel: Information & Logic */}
                   <div className="w-full md:w-3/5 border-r p-5 sm:p-6 pb-8 md:pb-6" style={{ borderColor: colors.border }}>
                       <div className="space-y-6">
                           
                           {/* Context Block */}
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="p-4 rounded-2xl" style={{ backgroundColor: colors.background }}>
                                 <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Initiated By</p>
                                 <div className="flex gap-2 items-center">
                                     <span className={`px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider shadow-sm text-white ${(!selectedDispute.refundRequested || selectedDispute.type === 'securityDepositClaim') ? 'bg-gradient-to-r from-orange-500 to-orange-400' : 'bg-gradient-to-r from-blue-500 to-blue-400'}`}>{(!selectedDispute.refundRequested || selectedDispute.type === 'securityDepositClaim') ? 'Host' : 'Guest'}</span>
                                     <span className="text-[13px] font-bold" style={{ color: colors.text }}>{(!selectedDispute.refundRequested || selectedDispute.type === 'securityDepositClaim') ? (hostData?.displayName || selectedDispute.hostId) : (guestData?.displayName || selectedDispute.guestId)}</span>
                                 </div>
                              </div>
                              <div className="p-4 rounded-2xl" style={{ backgroundColor: colors.background }}>
                                 <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Demand</p>
                                 {selectedDispute.refundRequested ? (
                                    <p className="text-[16px] font-black" style={{ color: colors.primary }}>
                                        {selectedDispute.requestedAmount ? formatCurrencyLocal(selectedDispute.requestedAmount) : 'Full Refund Required'}
                                    </p>
                                 ) : (
                                    <p className="text-[13px] font-bold" style={{ color: colors.textSecondary }}>Standard Resolution</p>
                                 )}
                              </div>
                           </div>

                           {/* Description */}
                           <div>
                              <p className="text-[11px] font-extrabold uppercase tracking-widest mb-2" style={{ color: colors.textMuted }}>Complaint Details</p>
                              <div className="p-4 rounded-2xl text-[14px] leading-relaxed" style={{ backgroundColor: colors.backgroundSecondary, color: colors.text }}>
                                  {selectedDispute.description}
                              </div>
                           </div>

                           {/* Booking Summary Box */}
                           <div className="border rounded-2xl overflow-hidden shadow-sm" style={{ borderColor: colors.border }}>
                               <div className="px-4 py-2 border-b text-[10px] font-extrabold uppercase tracking-widest" style={{ backgroundColor: colors.surfaceHover, borderColor: colors.border, color: colors.textSecondary }}>Transaction Context</div>
                               {loadingBooking ? (
                                   <div className="p-6 text-center text-[13px] font-medium animate-pulse" style={{ color: colors.textSecondary }}>Locating ledger references...</div>
                               ) : bookingData ? (
                                   <div className="p-4 space-y-3" style={{ backgroundColor: colors.background }}>
                                       {/* Property info */}
                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b" style={{ borderColor: colors.border }}>
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Property</p>
                                              <p className="text-[14px] font-bold" style={{ color: colors.text }}>{propertyData?.title || bookingData.propertyTitle || 'Unknown Property'}</p>
                                              <p className="text-[11px] font-mono mt-0.5" style={{ color: colors.textMuted }}>ID: {(bookingData.propertyId || selectedDispute.propertyId || '—')}</p>
                                           </div>
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Booking ID</p>
                                              <p className="text-[12px] font-mono font-bold break-all" style={{ color: colors.textSecondary }}>{bookingData.id}</p>
                                           </div>
                                       </div>
                                       {/* Dates */}
                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b" style={{ borderColor: colors.border }}>
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Check-In</p>
                                              <p className="text-[13px] font-bold" style={{ color: colors.text }}>
                                                {bookingData.checkIn?.toDate ? bookingData.checkIn.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : (bookingData.startDate?.toDate ? bookingData.startDate.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—')}
                                              </p>
                                           </div>
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Check-Out</p>
                                              <p className="text-[13px] font-bold" style={{ color: colors.text }}>
                                                {bookingData.checkOut?.toDate ? bookingData.checkOut.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : (bookingData.endDate?.toDate ? bookingData.endDate.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—')}
                                              </p>
                                           </div>
                                       </div>
                                       {/* Financials */}
                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Total Ledger Value</p>
                                              <p className="text-[15px] font-black" style={{ color: colors.text }}>{formatCurrencyLocal(bookingData.totalAmount || bookingData.totalPrice || bookingData.amount || 0)}</p>
                                           </div>
                                           <div>
                                              <p className="text-[11px] text-gray-500 font-medium mb-1">Security Deposit</p>
                                              <p className="text-[15px] font-bold" style={{ color: colors.purple }}>{selectedDispute.securityDepositAmount ? formatCurrencyLocal(selectedDispute.securityDepositAmount) : formatCurrencyLocal(bookingData.securityDepositAmount || 0)}</p>
                                           </div>
                                       </div>
                                   </div>
                               ) : (
                                   <div className="p-4 text-[13px] font-medium" style={{ color: colors.error }}>Database mismatch: Ledger not found.</div>
                               )}
                           </div>

                           {/* === ACTIONS PANEL === */}
                           {selectedDispute.status === 'open' && (
                               <div className="space-y-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                                   
                                   {selectedDispute.blocksEarnings && (
                                        <div className="p-3 rounded-xl flex items-center justify-between shadow-sm border" style={{ backgroundColor: colors.errorGhost, borderColor: `${colors.error}30`, color: colors.error }}>
                                           <div className="flex items-center gap-2">
                                              <AlertTriangle className="w-4 h-4" />
                                              <span className="text-[12px] font-bold uppercase tracking-wider">Host Earnings Frozen</span>
                                           </div>
                                        </div>
                                   )}

                                   {/* Guest Favorable block */}
                                   <div className="p-5 rounded-2xl shadow-sm border relative overflow-hidden" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                                        <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                                        <p className="text-[11px] font-extrabold uppercase tracking-widest mb-3" style={{ color: colors.textSecondary }}>Guest Compensation Protocols</p>
                                        <div className="space-y-2">
                                            <button
                                                onClick={() => {
                                                   const maxAmount = bookingData?.totalAmount || 99999;
                                                   const amount = prompt(`Enter partial refund amount for GUEST (max: SAR ${maxAmount}):`);
                                                   if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
                                                      if (parseFloat(amount) > maxAmount) return toast.error("Exceeds total ledger value");
                                                      handleResolveDispute(selectedDispute.id, 'partialRefund', parseFloat(amount));
                                                   }
                                                }}
                                                disabled={resolving}
                                                className="w-full text-left px-4 py-3 rounded-xl text-[13px] font-bold transition-all hover:bg-black/5"
                                                style={{ color: colors.primary, border: `1px solid ${colors.primary}40` }}
                                            >
                                                Partial Refund to Guest
                                            </button>
                                            <button
                                                onClick={() => {
                                                   const totalAmount = bookingData?.totalAmount || selectedDispute.requestedAmount || 0;
                                                   if (window.confirm(`Execute full refund of ${formatCurrencyLocal(totalAmount)} to guest ledger?`)) {
                                                      handleResolveDispute(selectedDispute.id, 'fullRefund', totalAmount);
                                                   }
                                                }}
                                                disabled={resolving}
                                                className="w-full text-left px-4 py-3 rounded-xl text-[13px] font-bold transition-all text-white shadow-md hover:-translate-y-0.5 relative z-10"
                                                style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}
                                            >
                                                Full Refund to Guest ({formatCurrencyLocal(bookingData?.totalAmount || selectedDispute.requestedAmount || 0)})
                                            </button>
                                        </div>
                                   </div>

                                   {/* Host Favorable block */}
                                   {/* Included if deposit claim, deposit exists, or host-filed */}
                                   {(selectedDispute.type === 'securityDepositClaim' || selectedDispute.affectsSecurityDeposit || bookingData?.securityDepositAmount > 0 || !selectedDispute.refundRequested) && (
                                       <div className="p-5 rounded-2xl shadow-sm border relative overflow-hidden" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                                            <div className="absolute top-0 left-0 w-1 h-full bg-purple-500"></div>
                                            <p className="text-[11px] font-extrabold uppercase tracking-widest mb-3" style={{ color: colors.textSecondary }}>Host Compensation Protocols</p>
                                            <div className="space-y-2">
                                                <button
                                                   onClick={() => {
                                                       const depositAmount = selectedDispute.securityDepositAmount || bookingData?.securityDepositAmount || 0;
                                                       const amount = prompt(`Award security deposit to host.\nDeposit on hold: SAR ${depositAmount}\nLeave blank to award full amount:`, depositAmount > 0 ? depositAmount.toString() : '');
                                                       if (amount !== null) {
                                                          const awardAmount = amount ? parseFloat(amount) : depositAmount;
                                                          if (isNaN(awardAmount) || awardAmount <= 0) return toast.error("Invalid amount");
                                                          handleResolveDispute(selectedDispute.id, 'securityDepositAwarded', null, { approvedDepositDeduction: awardAmount, adminAction: 'security_deposit_to_host', adminActionAmount: awardAmount });
                                                       }
                                                   }}
                                                   disabled={resolving}
                                                   className="w-full text-left px-4 py-3 rounded-xl text-[13px] font-bold transition-all text-white shadow-md hover:-translate-y-0.5"
                                                   style={{ background: 'linear-gradient(135deg, #9333EA 0%, #6B21A8 100%)' }}
                                                >
                                                    Award Security Deposit to Host
                                                </button>
                                                <button
                                                    onClick={() => {
                                                       if (window.confirm('Rule in favor of host? Earnings will unblock and guest receives no refund.')) {
                                                          handleResolveDispute(selectedDispute.id, 'noRefund', null, { adminAction: 'host_claim_approved' });
                                                       }
                                                    }}
                                                    disabled={resolving}
                                                    className="w-full text-left px-4 py-3 rounded-xl text-[13px] font-bold transition-all hover:bg-black/5"
                                                    style={{ color: '#9333EA', border: `1px solid #9333EA40` }}
                                                >
                                                    Rule in Favor of Host (No Action)
                                                </button>
                                            </div>
                                       </div>
                                   )}

                                   {/* Dismiss / Penalty block */}
                                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                      <button
                                          onClick={() => {
                                              if (window.confirm('Dismiss dispute completely? No refunds or compensations will be distributed.')) {
                                                 handleResolveDispute(selectedDispute.id, 'noRefund');
                                              }
                                          }}
                                          disabled={resolving}
                                          className="py-3 rounded-xl text-[12px] font-bold text-white shadow-md hover:-translate-y-0.5 transition-all"
                                          style={{ background: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' }}
                                      >
                                          Dismiss (No Action)
                                      </button>
                                      <button
                                          onClick={() => {
                                              const maxAmount = bookingData?.totalAmount || 99999;
                                              const amount = prompt(`Enter amount to DEDUCT from Host -> Guest:\nRemaining Ledger limits: SAR ${maxAmount}`);
                                              if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
                                                  handleResolveDispute(selectedDispute.id, 'hostDeductionWithRefund', parseFloat(amount), { adminAction: 'host_deduction_with_refund', hostDeductionAmount: parseFloat(amount) });
                                              }
                                          }}
                                          disabled={resolving}
                                          className="py-3 rounded-xl text-[12px] font-bold text-white shadow-md hover:-translate-y-0.5 transition-all"
                                          style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                                      >
                                          Deduct Host Penalty
                                      </button>
                                   </div>

                               </div>
                           )}

                           {/* Resolution View Mode */}
                           {selectedDispute.status === 'resolved' && (
                               <div className="p-5 rounded-2xl border bg-gradient-to-br from-green-500/10 to-emerald-600/10" style={{ borderColor: `${colors.success}30` }}>
                                   <p className="text-[11px] font-extrabold uppercase tracking-widest mb-4" style={{ color: colors.success }}>Resolution Verdict</p>
                                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-2 text-[13px]">
                                      <div><span className="opacity-70 font-semibold mb-0.5 block" style={{ color: colors.textMuted }}>Decision:</span> <span className="font-bold" style={{ color: colors.text }}>{{'noRefund': 'No Refund', 'partialRefund': 'Partial Refund', 'fullRefund': 'Full Refund', 'securityDepositAwarded': 'Deposit Awarded', 'hostDeductionWithRefund': 'Host Deduction'}[selectedDispute.resolution] || selectedDispute.resolution}</span></div>
                                      <div><span className="opacity-70 font-semibold mb-0.5 block" style={{ color: colors.textMuted }}>Concluded By:</span> <span className="font-bold" style={{ color: colors.text }}>{selectedDispute.resolvedBy}</span></div>
                                      {selectedDispute.refundAmount && <div><span className="opacity-70 font-semibold mb-0.5 block" style={{ color: colors.textMuted }}>Refund Remitted:</span> <span className="font-black" style={{ color: colors.primary }}>{formatCurrencyLocal(selectedDispute.refundAmount)}</span></div>}
                                      {selectedDispute.approvedDepositDeduction && <div><span className="opacity-70 font-semibold mb-0.5 block" style={{ color: colors.textMuted }}>Deposit Transferred:</span> <span className="font-black" style={{ color: '#9333EA' }}>{formatCurrencyLocal(selectedDispute.approvedDepositDeduction)}</span></div>}
                                   </div>
                                   <div className="mt-4 pt-3 border-t text-[11px] font-bold uppercase tracking-wider opacity-60" style={{ borderColor: `${colors.success}20`, color: colors.text }}>
                                      Stamped: {formatDateTimeLocal(selectedDispute.resolvedAt)}
                                   </div>
                               </div>
                           )}

                       </div>
                   </div>

                   {/* Right Panel: Chat Log */}
                   <div className="w-full md:w-2/5 min-h-[320px] md:min-h-0 flex flex-col" style={{ backgroundColor: colors.background }}>
                       <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                          <span className="text-[12px] font-extrabold uppercase tracking-widest" style={{ color: colors.textSecondary }}>Message Thread</span>
                          <MessageSquare className="w-4 h-4" style={{ color: colors.textMuted }} />
                       </div>
                       
                       <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin">
                           {selectedDispute.messages && selectedDispute.messages.length > 0 ? (
                               selectedDispute.messages.map((message, index) => {
                                  const isAdmin = message.senderRole === 'admin';
                                  const isSystem = message.senderRole === 'system';
                                  
                                  return (
                                     <div key={index} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] rounded-2xl p-3 ${isAdmin ? 'rounded-tr-sm text-white' : 'rounded-tl-sm'} ${isSystem ? 'bg-orange-500/10 text-orange-600 border border-orange-500/20 text-center mx-auto' : ''}`}
                                            style={{ 
                                               backgroundColor: isAdmin ? colors.primary : (isSystem ? undefined : colors.surface), 
                                               border: isAdmin ? 'none' : (isSystem ? undefined : `1px solid ${colors.border}`),
                                               color: isAdmin ? 'white' : colors.text
                                            }}
                                        >
                                            {!isSystem && <div className="flex justify-between items-center mb-1.5 gap-4">
                                                <span className={`text-[9px] font-black uppercase tracking-widest ${isAdmin ? 'text-white/80' : 'text-primary'}`}>
                                                    {isAdmin ? 'Admin' : (message.senderRole === 'host' ? 'Host' : 'Guest')}
                                                </span>
                                            </div>}
                                            <p className={`text-[13px] ${isSystem ? 'font-bold' : ''} leading-relaxed whitespace-pre-wrap`}>{message.message}</p>
                                            <div className={`text-[9px] font-bold mt-2 text-right ${isAdmin ? 'text-white/60' : 'opacity-50'}`} style={{ color: !isAdmin ? colors.textSecondary : undefined }}>
                                                {formatDateTimeLocal(message.timestamp?.toDate ? message.timestamp.toDate() : new Date(message.timestamp))}
                                            </div>
                                        </div>
                                     </div>
                                  )
                               })
                           ) : (
                               <div className="h-full flex flex-col items-center justify-center text-center">
                                   <div className="w-12 h-12 rounded-full mb-3 flex items-center justify-center opacity-50" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                                      <MessageSquare className="w-5 h-5" style={{ color: colors.textMuted }} />
                                   </div>
                                   <p className="text-[13px] font-bold" style={{ color: colors.textSecondary }}>No internal communication yet.</p>
                                   <p className="text-[11px] mt-1 pr-4 pl-4 leading-relaxed" style={{ color: colors.textMuted }}>Admins, Guests, and Hosts can use this thread to send evidence or statements.</p>
                               </div>
                           )}
                       </div>

                       {/* Chat Input */}
                       {selectedDispute.status === 'open' && (
                           <div className="p-4 border-t" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                               <div className="flex gap-2 relative">
                                   <input
                                      type="text"
                                      value={newMessage}
                                      onChange={(e) => setNewMessage(e.target.value)}
                                      placeholder="Type message to participants..."
                                      className="flex-1 pl-4 pr-12 py-3.5 rounded-xl text-[13px] font-medium shadow-sm transition-all focus:outline-none"
                                      style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                                      onKeyPress={(e) => { if (e.key === 'Enter') handleSendMessage(selectedDispute.id); }}
                                   />
                                   <button
                                      onClick={() => handleSendMessage(selectedDispute.id)}
                                      disabled={sendingMessage || !newMessage.trim()}
                                      className="absolute right-2 top-1.5 bottom-1.5 px-4 rounded-lg bg-blue-600 text-[11px] font-black uppercase tracking-wider text-white shadow-md disabled:opacity-50 hover:bg-blue-700 transition-colors"
                                   >
                                      {sendingMessage ? '...' : 'Send'}
                                   </button>
                               </div>
                           </div>
                       )}

                   </div>
               </div>

            </div>
            </div>
         </div>
      )}
    </div>
  );
};

export default Disputes;
